import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import FilterFieldLabel from "./FilterFieldLabel";

const { RangePicker } = DatePicker;
const DATE_FORMAT = "YYYY-MM-DD";

/**
 * DateRangeFilter - From/to date range filter grouped under a single label.
 *
 * Fully controlled. The current range is read from the `fromValue` / `toValue`
 * "YYYY-MM-DD" strings (URL search params). On change it calls `onChange` with
 * an object keyed by `fromName` / `toName` so it can be passed straight to
 * `handleFilterChange("range", obj, ...)` (which spreads object values into the
 * search params). Clearing the range sets both keys to null.
 *
 * @param {string} label - Group label (e.g. "Created Date")
 * @param {string} fromName - Search param key for the start date (default "from")
 * @param {string} toName - Search param key for the end date (default "to")
 * @param {string} fromValue - Current start date "YYYY-MM-DD"
 * @param {string} toValue - Current end date "YYYY-MM-DD"
 * @param {(value: Record<string, string|null>) => void} onChange
 * @param {string} hint - Optional info tooltip rendered next to the label
 * @param {string} className
 */
const DateRangeFilter = ({
  label,
  fromName = "from",
  toName = "to",
  fromValue,
  toValue,
  onChange,
  hint,
  className = "h-[44px] w-full filter-popup-item",
}) => {
  const { t } = useTranslation();

  const from =
    fromValue && dayjs(fromValue).isValid() ? dayjs(fromValue) : null;
  const to = toValue && dayjs(toValue).isValid() ? dayjs(toValue) : null;
  const rangeValue = from && to ? [from, to] : null;

  const handleChange = (dates) => {
    if (dates && dates.length === 2) {
      onChange({
        [fromName]: dates[0].format(DATE_FORMAT),
        [toName]: dates[1].format(DATE_FORMAT),
      });
    } else {
      onChange({ [fromName]: null, [toName]: null });
    }
  };

  return (
    <div className="w-full">
      <FilterFieldLabel label={label} hint={hint} />
      <RangePicker
        value={rangeValue}
        onChange={handleChange}
        format={DATE_FORMAT}
        placeholder={[t("from"), t("to")]}
        className={className}
        allowClear
      />
    </div>
  );
};

export default DateRangeFilter;
